import { type FC, useMemo } from "react";

import { Pencil } from "lucide-react";

import { IconButton } from "@/shared/components/ui/icon-button";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import type { AdminChildRow } from "./adminFamilyTypes";

function hasLivingDescendant(answers: Record<string, string | string[]>): boolean {
  const hasChildren = answers["admin_family_has_children"];
  if (hasChildren !== "yes") return false;
  try {
    const list = answers["admin_family_children_list"];
    if (typeof list !== "string") return false;
    const children = JSON.parse(list) as AdminChildRow[];
    const check = (rows: AdminChildRow[]): boolean => {
      for (const row of rows) {
        if (row.status === "living") return true;
        if (row.status === "passed_away" && row.hasChildren === "yes" && check(row.children)) return true;
      }
      return false;
    };
    return check(children);
  } catch {
    return false;
  }
}

function extendedFamilyScreenWasInFlow(answers: Record<string, string | string[]>): boolean {
  const married = answers["admin_family_married_at_death"];
  const spouseStatus = answers["admin_family_spouse_status"];
  const hasLivingSpouse = married === "yes" && spouseStatus === "living";
  const hasDescendant = hasLivingDescendant(answers);
  return (hasLivingSpouse && !hasDescendant) || (!hasLivingSpouse && !hasDescendant);
}

type ScreenId =
  | "admin_family_triage_marriages"
  | "admin_family_triage_children"
  | "admin_family_triage_extended";

interface ReviewRow {
  name: string;
  relationship: string;
  status: string;
  editScreen: ScreenId;
}

function statusLabel(status: string): string {
  if (status === "living") return "Living";
  if (status === "passed_away") return "Passed away";
  return "";
}

function collectDescendants(
  children: AdminChildRow[],
  relationship: string,
  editScreen: ScreenId,
  rows: ReviewRow[],
) {
  for (const child of children) {
    if (child.firstName || child.lastName) {
      rows.push({
        name: `${child.firstName} ${child.lastName}`.trim(),
        relationship,
        status: statusLabel(child.status),
        editScreen,
      });
    }
    if (child.status === "passed_away" && child.hasChildren === "yes") {
      const nextRel = relationship === "Child" ? "Grandchild" : "Great-grandchild";
      collectDescendants(child.children, nextRel, editScreen, rows);
    }
  }
}

export const AdminReviewScreen: FC<FlowCustomComponentProps> = ({ answers, goToScreen }) => {
  const rows = useMemo((): ReviewRow[] => {
    const result: ReviewRow[] = [];

    const married = answers["admin_family_married_at_death"];
    if (married === "yes") {
      const first = (answers["admin_family_spouse_first_name"] as string) ?? "";
      const last = (answers["admin_family_spouse_last_name"] as string) ?? "";
      const status = (answers["admin_family_spouse_status"] as string) ?? "";
      if (first || last) {
        result.push({
          name: `${first} ${last}`.trim(),
          relationship: "Spouse",
          status: statusLabel(status),
          editScreen: "admin_family_triage_marriages",
        });
      }
    }

    try {
      const list = answers["admin_family_children_list"];
      if (typeof list === "string" && list !== "[]") {
        collectDescendants(JSON.parse(list) as AdminChildRow[], "Child", "admin_family_triage_children", result);
      }
    } catch { /* skip */ }

    if (extendedFamilyScreenWasInFlow(answers)) {
      try {
        const list = answers["admin_family_parents_list"];
        if (typeof list === "string" && list !== "[]") {
          const parents = JSON.parse(list) as AdminChildRow[];
          for (const p of parents) {
            if (p.firstName || p.lastName) {
              result.push({
                name: `${p.firstName} ${p.lastName}`.trim(),
                relationship: "Parent",
                status: statusLabel(p.status),
                editScreen: "admin_family_triage_extended",
              });
            }
          }
        }
      } catch { /* skip */ }

      try {
        const list = answers["admin_family_siblings_list"];
        if (typeof list === "string" && list !== "[]") {
          const siblings = JSON.parse(list) as AdminChildRow[];
          for (const s of siblings) {
            if (s.firstName || s.lastName) {
              result.push({
                name: `${s.firstName} ${s.lastName}`.trim(),
                relationship: "Sibling",
                status: statusLabel(s.status),
                editScreen: "admin_family_triage_extended",
              });
            }
            if (s.status === "passed_away" && s.hasChildren === "yes") {
              for (const n of s.children) {
                if (n.firstName || n.lastName) {
                  result.push({
                    name: `${n.firstName} ${n.lastName}`.trim(),
                    relationship: "Niece/Nephew",
                    status: statusLabel(n.status),
                    editScreen: "admin_family_triage_extended",
                  });
                }
              }
            }
          }
        }
      } catch { /* skip */ }
    }

    return result;
  }, [answers]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="font-sans text-xl font-semibold leading-[30px]">
          Let's make sure we have the facts right
        </p>
        <p className="text-sm text-secondary tracking-[-0.28px]">
          Based on your answers, these are the family members the court will include on your
          administration forms. Make sure everyone listed is correct, or make edits below.
        </p>
      </div>

      {rows.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-primary">
          {rows.map((row, i) => (
            <div
              key={`${row.name}-${i}`}
              className={`flex items-center gap-4 bg-white p-4 ${i > 0 ? "border-t border-primary" : ""}`}
            >
              <div className="flex flex-1 flex-col gap-0.5 min-w-0">
                <p className="text-base font-semibold tracking-[-0.32px]">{row.name}</p>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-secondary tracking-[-0.28px]">{row.relationship}</p>
                  {row.status && (
                    <>
                      <span className="size-1 shrink-0 rounded-full bg-tertiary" aria-hidden="true" />
                      <p className="text-sm text-secondary tracking-[-0.28px]">{row.status}</p>
                    </>
                  )}
                </div>
              </div>
              <IconButton
                label={`Edit ${row.name}`}
                variant="neutral"
                onClick={() => goToScreen(row.editScreen)}
              >
                <Pencil />
              </IconButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
